/**
 * Composable for managing Auth0 token refresh and session expiration
 *
 * This composable provides:
 * - Automatic token refresh before expiration (5 minute buffer)
 * - Session expiration detection
 * - Exponential backoff on refresh failures
 * - Handling of token refresh failures
 */

import { ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { isTokenExpired, shouldRefreshToken } from '@/utils/token.utils'
import { emitSessionExpired } from '@/utils/auth-error-handler'

// Token refresh interval - check every 3 minutes (balance between responsiveness and API load)
const TOKEN_CHECK_INTERVAL = 3 * 60 * 1000 // 3 minutes

// Proactive refresh buffer - refresh tokens 5 minutes before expiration
const REFRESH_BUFFER_SECONDS = 5 * 60 // 5 minutes

export function useTokenRefresh(onSessionExpired?: () => void) {
  const authStore = useAuthStore()
  const isRefreshing = ref(false)
  let intervalId: number | null = null
  let errorCount = 0
  const MAX_ERRORS = 3 // Stop polling after 3 consecutive errors
  let lastRefreshAttempt = 0
  let backoffDelay = 0

  /** Convenience getter so forceRefreshToken can be called without a store wrapper */
  const authService = () => authStore.getAuthService()

  /**
   * Calculate exponential backoff delay
   */
  function calculateBackoff(): number {
    // Exponential backoff: 2^errorCount seconds (2s, 4s, 8s)
    return Math.min(Math.pow(2, errorCount) * 1000, 8000)
  }

  /**
   * Check if enough time has passed since last refresh attempt (for backoff)
   */
  function canAttemptRefresh(): boolean {
    const now = Date.now()
    return now - lastRefreshAttempt >= backoffDelay
  }

  /**
   * Check if the current token needs to be refreshed
   */
  async function checkAndRefreshToken(): Promise<void> {
    if (!authStore.isAuthenticated || isRefreshing.value) {
      return
    }

    // Stop polling if we've had too many consecutive errors
    if (errorCount >= MAX_ERRORS) {
      stopMonitoring()
      handleSessionExpired()
      return
    }

    // Respect exponential backoff
    if (!canAttemptRefresh()) {
      return
    }

    try {
      isRefreshing.value = true
      lastRefreshAttempt = Date.now()

      // Get the current token (Auth0 SDK handles caching automatically)
      // This will return cached token if valid, or refresh if needed
      const tokenResult = await authStore.refreshToken()

      if (!tokenResult.ok) {
        errorCount++
        backoffDelay = calculateBackoff()

        // Only trigger session expired after multiple failures to avoid false positives
        if (errorCount >= MAX_ERRORS) {
          handleSessionExpired()
        }
        return
      }

      const token = tokenResult.value

      // Check if the token is already expired
      if (isTokenExpired(token)) {
        handleSessionExpired()
        return
      }

      // Check if token should be refreshed proactively (within 5 minutes of expiration).
      // IMPORTANT: We MUST force the refresh here while the token is still in the Auth0
      // cache. If we wait, Auth0's internal ~60-second leeway will remove the token
      // AND its associated refresh token from localStorage. Once gone, getTokenSilently()
      // cannot use the refresh-token grant and silent iframe auth fails, causing sign-out.
      const needsProactiveRefresh = shouldRefreshToken(token, REFRESH_BUFFER_SECONDS)

      if (needsProactiveRefresh) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log('[TokenRefresh] Token approaching expiration — attempting proactive refresh')
        }

        // Attempt forceRefreshToken (cacheMode: 'off') to use the refresh-token grant NOW,
        // while the token and refresh token are still present in the Auth0 cache.
        // IMPORTANT: Do NOT increment errorCount on proactive refresh failure — the current
        // token is still valid (refreshToken() succeeded above). Proactive refresh is
        // best-effort; failures are expected when no refresh token is available (e.g. when
        // the Auth0 API audience does not support offline_access / refresh tokens).
        // Only increment errorCount when getAccessToken() itself fails (token truly unusable).
        const forceResult = await authService()?.forceRefreshToken()
        if (forceResult?.ok) {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log('[TokenRefresh] Proactive refresh succeeded — session extended')
          }
        } else {
          if (import.meta.env.DEV) {
            console.warn(
              '[TokenRefresh] Proactive refresh failed (no refresh token or silent auth blocked) — token still valid, will expire naturally'
            )
          }
          // Fall through to reset errorCount below — token is still currently valid
        }
      }

      // Reset error count and backoff on success
      errorCount = 0
      backoffDelay = 0
    } catch (_error) {
      errorCount++
      backoffDelay = calculateBackoff()

      // Only trigger session expired after multiple failures
      if (errorCount >= MAX_ERRORS) {
        handleSessionExpired()
      }
    } finally {
      isRefreshing.value = false
    }
  }

  /**
   * Handle session expiration
   */
  function handleSessionExpired(): void {
    // Emit global session expired event
    emitSessionExpired()

    // Call local callback if provided
    if (onSessionExpired) {
      onSessionExpired()
    }
  }

  /**
   * Start monitoring token expiration
   */
  function startMonitoring(): void {
    if (intervalId) {
      return
    }

    // Reset error count to ensure clean state
    errorCount = 0

    // Check immediately
    checkAndRefreshToken()

    // Set up periodic checks
    intervalId = window.setInterval(() => {
      checkAndRefreshToken()
    }, TOKEN_CHECK_INTERVAL)
  }

  /**
   * Stop monitoring token expiration
   */
  function stopMonitoring(): void {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  // Lifecycle hooks
  onMounted(() => {
    if (authStore.isAuthenticated) {
      startMonitoring()
    }
  })

  onUnmounted(() => {
    stopMonitoring()
  })

  return {
    isRefreshing,
    checkAndRefreshToken,
    startMonitoring,
    stopMonitoring,
  }
}
